import {
  OnboardingDialogOption,
  openBankAccountDialog,
  openBankLegalNameDialog,
  openLegalNameDialog,
} from "@components/utils/OnboardingDialog/utils";
import { Paths } from "@constants/paths";
import { PostLoginInfoType } from "@types";
import { MemberSettingFundingMode } from "kz-ui-sdk";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthSession from "./useAuthSession";
import { useMemberSettings } from "./useMemberSettings";
/**
 * After login/register, user need to provide required info
 * There are 2 types of required info based on member settings fundingMode:
 * 1. Bank account
 * 2. Legal name
 *
 * return {
 *  type: PostLoginInfoType | null,
 *  isFulfilled: boolean (default required info based on server flag)
 *  isBankAccountFulfilled: boolean (bank account is fulfilled)
 *  isLegalNameFulfilled: boolean (legal name is fulfilled)
 *  triggerForm: trigger the form to update the info
 * }
 */
const usePostLoginInfo = () => {
  const { fundingMode } = useMemberSettings();
  const { defaultBankAccount, legalName } = useAuthSession();
  const navigate = useNavigate();
  const pathname = useLocation().pathname;

  const requiredInfo = useMemo(() => {
    let type: PostLoginInfoType | null = null;
    let isFulfilled = false;

    switch (fundingMode) {
      case MemberSettingFundingMode.BankOnly:
        type = PostLoginInfoType.BANK_ACCOUNT;
        isFulfilled = !!defaultBankAccount;
        break;
      case MemberSettingFundingMode.EWalletOnly:
        type = PostLoginInfoType.LEGAL_NAME;
        isFulfilled = !!legalName;
        break;
      case MemberSettingFundingMode.BankEWallet:
        type = PostLoginInfoType.BANK_LEGAL_NAME;
        isFulfilled = !!legalName;
        break;
      default:
        type = PostLoginInfoType.BANK_ACCOUNT;
        isFulfilled = !!defaultBankAccount;
        break;
    }

    return {
      type,
      isFulfilled,
      isBankAccountFulfilled: !!defaultBankAccount,
      isLegalNameFulfilled: !!legalName,
    };
  }, [fundingMode, defaultBankAccount, legalName]);

  const triggerForm = useCallback(
    (formType: "dialog" | "full-page", onUpdateSuccess?: () => void, options?: OnboardingDialogOption) => {
      const requiredInfoType = options?.requiredInfo ?? requiredInfo.type;

      if (formType === "dialog") {
        switch (requiredInfoType) {
          case PostLoginInfoType.BANK_ACCOUNT:
            // ignore drawer if already in page mode
            if (pathname === Paths.PRIVATE.AUTH_BANK_ACCOUNT_PAGE) return;
            openBankAccountDialog(onUpdateSuccess, options);
            break;
          case PostLoginInfoType.LEGAL_NAME:
            // ignore drawer if already in page mode
            if (pathname === Paths.PRIVATE.AUTH_LEGAL_NAME_PAGE) return;
            openLegalNameDialog(onUpdateSuccess, options);
            break;
          case PostLoginInfoType.BANK_LEGAL_NAME:
            // ignore drawer if already in page mode
            if (pathname === Paths.PRIVATE.AUTH_BANK_LEGAL_NAME_PAGE) return;
            openBankLegalNameDialog(onUpdateSuccess, options);
            break;
        }
      }
      if (formType === "full-page") {
        switch (requiredInfoType) {
          case PostLoginInfoType.BANK_ACCOUNT:
            navigate(Paths.PRIVATE.AUTH_BANK_ACCOUNT_PAGE, {
              replace: true,
            });
            break;
          case PostLoginInfoType.LEGAL_NAME:
            navigate(Paths.PRIVATE.AUTH_LEGAL_NAME_PAGE, {
              replace: true,
            });
            break;
          case PostLoginInfoType.BANK_LEGAL_NAME:
            navigate(Paths.PRIVATE.AUTH_BANK_LEGAL_NAME_PAGE, {
              replace: true,
            });
            break;
        }
      }
    },
    [requiredInfo.type, navigate, pathname],
  );

  return {
    ...requiredInfo,
    triggerForm,
  };
};

export default usePostLoginInfo;
